import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import API from "../api";
import AlertModal from "./AlertModal";
import ConfirmModal from "./ConfirmModal";
import { CURRENT_SEASON } from "../constants/season";

const initials = (name) => name?.slice(0, 2).toUpperCase() || "??";

/** Your private league: standings up front, invite on the same screen. */
export default function GroupsCard({
  competition = "premier_league",
  season = CURRENT_SEASON,
  currentUsername = null,
  onCompare,
  onMeta,
}) {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [showPublic, setShowPublic] = useState(false);
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: "", message: "", type: "info" });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: "", message: "", onConfirm: null });

  const { data: publicGroups = [] } = useQuery({
    queryKey: ["publicGroups"],
    queryFn: () => API.get("/groups/public").then((res) => res.data),
  });

  const { data: groups = [], isError: groupsError, isLoading: groupsLoading } = useQuery({
    queryKey: ["myGroups"],
    queryFn: () => API.get("/groups").then((res) => res.data),
  });

  const group = groups[0] || null;

  const {
    data: board,
    isLoading: boardLoading,
    isError: boardError,
  } = useQuery({
    queryKey: ["groupBoard", group?.id, competition, season],
    enabled: Boolean(group?.id),
    queryFn: async () => {
      const [{ data }, picksRes] = await Promise.all([
        API.get(`/groups/${group.id}/leaderboard`, { params: { competition, season } }),
        API.get(`/groups/${group.id}/locked-picks`, { params: { competition, season } }),
      ]);
      return { rows: Array.isArray(data) ? data : [], picks: picksRes.data || [] };
    },
  });

  const rows = board?.rows || [];
  const picks = board?.picks || [];

  useEffect(() => {
    if (!onMeta || groupsLoading) return;
    if (!group) {
      onMeta("Create a league or join with a code");
      return;
    }
    const list = board?.rows || [];
    const me = list.find((row) => row.username === currentUsername);
    if (!me || list.length === 0) {
      onMeta(group.name);
      return;
    }
    const gap = (Number(list[0].points) || 0) - (Number(me.points) || 0);
    onMeta(
      `${group.name} · #${me.rank} of ${list.length}${gap > 0 ? ` · ${gap} pts behind` : " · top of the table"}`
    );
  }, [onMeta, groupsLoading, group, board, currentUsername]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["myGroups"] });
    queryClient.invalidateQueries({ queryKey: ["publicGroups"] });
    queryClient.invalidateQueries({ queryKey: ["groupBoard"] });
  };

  const createGroupMutation = useMutation({
    mutationFn: (data) => API.post("/groups", data),
    onSuccess: () => {
      invalidate();
      setShowCreate(false);
      setNewGroupName("");
      setIsPublic(false);
    },
    onError: (e) => {
      setAlertModal({
        isOpen: true,
        title: "Couldn't create the league",
        message: e.response?.data?.message || "Try again in a moment.",
        type: "error",
      });
    },
  });

  const joinGroupMutation = useMutation({
    mutationFn: (code) => API.post("/groups/join", { join_code: code }),
    onSuccess: () => {
      invalidate();
      setJoinCode("");
    },
    onError: (e) => {
      setAlertModal({
        isOpen: true,
        title: "Couldn't join",
        message: e.response?.data?.message || "Check the code and try again.",
        type: "error",
      });
    },
  });

  const joinPublicMutation = useMutation({
    mutationFn: (groupId) => API.post("/groups/join-public", { group_id: groupId }),
    onSuccess: invalidate,
    onError: (e) => {
      setAlertModal({
        isOpen: true,
        title: "Couldn't join",
        message: e.response?.data?.message || "Try again in a moment.",
        type: "error",
      });
    },
  });

  const leaveGroupMutation = useMutation({
    mutationFn: (id) => API.delete(`/groups/${id}/leave`),
    onSuccess: invalidate,
    onError: () => {
      setAlertModal({ isOpen: true, title: "Couldn't leave", message: "Try again in a moment.", type: "error" });
    },
  });

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(() =>
      setAlertModal({ isOpen: true, title: "Copied", message: "Invite code copied.", type: "success" })
    );
  };

  const publicList = (
    <div className="grid gap-3 md:grid-cols-2">
      {publicGroups.length === 0 ? (
        <p className="col-span-full py-2 text-center text-sm text-muted">No public leagues right now.</p>
      ) : (
        publicGroups.map((item) => {
          const joined = groups.some((mine) => mine.id === item.id);
          return (
            <div key={item.id} className="rounded-lg border border-line p-4">
              <h3 className="font-semibold text-ink">{item.name}</h3>
              <p className="mb-3 mt-1 text-meta text-muted">{item.member_count} members</p>
              <button
                type="button"
                onClick={() => joinPublicMutation.mutate(item.id)}
                disabled={joined || Boolean(group)}
                className="h-11 w-full rounded-lg bg-brand text-sm font-semibold text-white hover:bg-brand-hover disabled:bg-gray-200 disabled:text-gray-500"
              >
                {joined ? "Joined" : group ? "Already in a league" : "Join"}
              </button>
            </div>
          );
        })
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {groupsError ? (
        <p className="rounded-xl border border-line bg-white px-4 py-8 text-center text-sm text-red-700">
          Couldn't load your league. Refresh and try again.
        </p>
      ) : groupsLoading ? (
        <div className="divide-y divide-line rounded-xl border border-line bg-white shadow-sm" aria-hidden>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex h-14 items-center gap-3 px-4">
              <div className="h-4 w-6 rounded bg-gray-200" />
              <div className="h-8 w-8 rounded-full bg-gray-200" />
              <div className="h-4 w-28 rounded bg-gray-200" />
            </div>
          ))}
        </div>
      ) : !group ? (
        <section className="rounded-xl border border-line bg-white p-4 shadow-sm md:p-5">
          <h2 className="text-[15px] font-semibold text-ink">Start a league</h2>
          <p className="mt-1 text-sm text-muted">One league with your mates. You get a code to share after you create it.</p>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="mt-4 inline-flex h-11 items-center rounded-lg bg-brand px-4 text-sm font-semibold text-white hover:bg-brand-hover"
          >
            Create a league
          </button>
          <form
            className="mt-4 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (joinCode.trim()) joinGroupMutation.mutate(joinCode.trim());
            }}
          >
            <input
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="Join code"
              aria-label="Join code"
              className="h-11 min-w-0 flex-1 rounded-lg border border-line px-3 font-mono uppercase outline-none focus:border-brand"
            />
            <button
              type="submit"
              disabled={!joinCode.trim() || joinGroupMutation.isPending}
              className="h-11 rounded-lg bg-brand px-4 text-sm font-semibold text-white hover:bg-brand-hover disabled:bg-gray-200 disabled:text-gray-500"
            >
              Join
            </button>
          </form>
        </section>
      ) : (
        <section className="overflow-hidden rounded-xl border border-line bg-white shadow-sm">
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 md:px-5">
            <div className="min-w-0">
              <h2 className="truncate text-[15px] font-semibold text-ink">{group.name}</h2>
              <p className="text-meta text-muted">
                {group.member_count} {group.member_count === 1 ? "member" : "members"} · code{" "}
                <span className="font-mono text-ink">{group.join_code}</span>
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => copyCode(group.join_code)}
                className="h-11 rounded-lg bg-brand px-3 text-sm font-semibold text-white hover:bg-brand-hover"
              >
                Copy invite
              </button>
              <button
                type="button"
                onClick={() =>
                  setConfirmModal({
                    isOpen: true,
                    title: "Leave league",
                    message: "Leave this league? You can rejoin with the code.",
                    onConfirm: () => {
                      setConfirmModal({ isOpen: false, title: "", message: "", onConfirm: null });
                      leaveGroupMutation.mutate(group.id);
                    },
                  })
                }
                className="h-11 rounded-lg border border-line px-3 text-sm font-semibold text-red-700 hover:border-red-300"
              >
                Leave
              </button>
            </div>
          </header>

          {boardLoading ? (
            <p className="px-4 py-8 text-center text-sm text-muted">Loading the table…</p>
          ) : boardError ? (
            <p className="px-4 py-8 text-center text-sm text-red-700">Couldn't load the table.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">{group.name} table</caption>
                <thead className="bg-gray-50 text-[12px] font-medium text-muted">
                  <tr>
                    <th scope="col" className="h-9 w-12 px-4 text-right font-medium">#</th>
                    <th scope="col" className="h-9 px-2 text-left font-medium">Player</th>
                    <th scope="col" className="hidden h-9 w-24 px-2 text-right font-medium sm:table-cell">This week</th>
                    <th scope="col" className="h-9 w-16 px-2 text-right font-medium">Points</th>
                    <th scope="col" className="h-9 w-24 px-3 text-right font-medium"><span className="sr-only">Compare</span></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const isMe = currentUsername != null && row.username === currentUsername;
                    return (
                      <tr key={row.id} aria-current={isMe ? "true" : undefined} className={`border-t border-line ${isMe ? "bg-brand-soft" : ""}`}>
                        <th scope="row" className="h-14 px-4 text-right font-semibold tabular-nums text-gray-700">{row.rank}</th>
                        <td className="h-14 px-2">
                          <span className="flex min-w-0 items-center gap-2">
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-bold text-gray-600">
                              {initials(row.username)}
                            </span>
                            <span className="truncate font-medium text-ink">
                              {row.username}
                              {isMe && <span className="ml-2 text-[12px] font-semibold text-brand">You</span>}
                            </span>
                          </span>
                        </td>
                        <td className="hidden h-14 px-2 text-right tabular-nums text-muted sm:table-cell">{row.recent_points ?? 0}</td>
                        <td className="h-14 px-2 text-right font-bold tabular-nums text-ink">{row.points}</td>
                        <td className="h-14 px-3 text-right">
                          {!isMe && onCompare && (
                            <button
                              type="button"
                              onClick={() => onCompare(row.username)}
                              className="hidden h-11 px-2 text-[12px] font-semibold text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand sm:inline"
                            >
                              Compare
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {picks.length > 0 && (
        <details className="rounded-xl border border-line bg-white shadow-sm">
          <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-ink">Picks after lock</summary>
          <div className="space-y-3 border-t border-line px-4 py-3">
            {picks.map((fixture) => (
              <div key={fixture.fixture_id}>
                <p className="text-sm font-medium text-ink">
                  {fixture.home_team} {fixture.home_score ?? "–"}–{fixture.away_score ?? "–"} {fixture.away_team}
                </p>
                <ul className="mt-1 space-y-0.5 text-[12px] text-muted">
                  {fixture.picks.map((pick) => (
                    <li key={pick.username} className="flex justify-between gap-2">
                      <span className="truncate">{pick.username}</span>
                      <span className="shrink-0 tabular-nums">
                        {pick.home}–{pick.away}
                        {pick.points != null ? ` · ${pick.points}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </details>
      )}

      <section className="rounded-xl border border-line bg-white shadow-sm">
        <button
          type="button"
          onClick={() => setShowPublic((open) => !open)}
          aria-expanded={showPublic}
          className="flex h-12 w-full items-center justify-between px-4 text-left text-sm font-semibold text-ink"
        >
          Public leagues
          <span className="text-[12px] font-medium text-muted">{showPublic ? "Hide" : "Show"}</span>
        </button>
        {showPublic && <div className="border-t border-line px-4 py-3">{publicList}</div>}
      </section>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative w-80 max-w-full rounded-xl border border-line bg-white p-6 shadow-lg">
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              aria-label="Close"
              className="absolute right-4 top-4 text-sm text-gray-500 hover:text-ink"
            >
              Close
            </button>
            <h2 className="mb-4 text-lg font-semibold text-ink">Create a league</h2>
            <input
              className="mb-4 h-11 w-full rounded-lg border border-gray-300 px-3 text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="League name"
              aria-label="League name"
            />
            <label className="mb-5 flex cursor-pointer items-center">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="mr-2 h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Anyone can join</span>
            </label>
            <button
              type="button"
              onClick={() => {
                if (newGroupName.trim()) createGroupMutation.mutate({ name: newGroupName.trim(), is_public: isPublic });
              }}
              disabled={createGroupMutation.isPending || !newGroupName.trim()}
              className="h-11 w-full rounded-lg bg-brand font-semibold text-white hover:bg-brand-hover disabled:opacity-50"
            >
              {createGroupMutation.isPending ? "Creating…" : "Create league"}
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, title: "", message: "", onConfirm: null })}
      />
      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ isOpen: false, title: "", message: "", type: "info" })}
      />
    </div>
  );
}
